import './index.scss';
import React, { useContext } from 'react';
import { FunctionComponent, useEffect, useState } from 'react';
import { useHistory } from 'react-router';
import { Field } from 'formik';
import dayjs from 'dayjs';
import Box from '@mui/material/Box';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import TextField from '@mui/material/TextField';
import Skeleton from '@mui/material/Skeleton';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Stack from '@mui/material/Stack';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import { Autocomplete, createFilterOptions } from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import {
  AgreementTypes,
  IClaim,
  IClaimForm,
  IWorkshopInfo,
  Payments,
  ReceiveCheckType,
  SecurityQuestion,
} from '@src/types/claimsTracker';
import {
  getBanks,
  IInspectionContext,
  InspectionContext,
} from '@context/inspections';
import Signature from '@components/common/signature';
import ClaimsTrackerClient from '@shared/clients/ClaimsTrackerClient';
import CATALOGS from '@static/constants/catalogs';
import Button from '../button';
import { ActionType } from '@src/types/action';
import { formatNumberPoint } from '@shared/utils/';
import { BrandsId } from '@src/types/common';
import QuestionList from '@components/common/questionsList/QuestionList';
import ClaimTrackerClient from '@shared/clients/ClaimsTrackerClient';

interface OptionBank {
  inputValue?: string;
  name: string;
}

interface Props {
  claimUId: string;
  claimForm?: IClaimForm;
  disabled: boolean;
  disableFinishActionState: (status: boolean) => void;
  legacyCurrentClaim?: IClaim;
}

interface PaymentType {
  label: string;
  value: AgreementTypes | ReceiveCheckType | Payments;
}

type TypePayment = { [key in number]: PaymentType[] };

const NOT_AVAILABLE = 'N/D';

const cleanValue = (value?: string | null): string =>
  !value || value === NOT_AVAILABLE ? '' : value;

const buildAchFields = (form?: IClaimForm): Record<string, string> => {
  const detail = form?.info?.paymentDetail;

  if (!detail) {
    return {};
  }

  return {
    custom_claimentName: cleanValue(detail.claimentName),
    custom_phone: cleanValue(detail.phone),
    custom_routeNumber: cleanValue(detail.routeNumber),
    custom_nameBank: cleanValue(detail.nameBank),
    custom_accountNumber: cleanValue(detail.accountNumber),
    custom_accountType: cleanValue(detail.accountType),
  };
};

const AgreementDetail: FunctionComponent<Props> = ({
  claimUId,
  claimForm,
  disabled,
  disableFinishActionState,
  legacyCurrentClaim,
}): JSX.Element => {
  const { state: claimState, dispatch } = useContext<IInspectionContext>(
    InspectionContext,
  );
  const history = useHistory();

  const [editByAnalyst, setEditByAnalyst] = useState<boolean>(false);
  const [openConfirm, setOpenConfirm] = useState<boolean>(false);
  const [openConfirmEdit, setOpenConfirmEdit] = useState<boolean>(false);
  const [openConfirmBySupport, setOpenConfirmBySupport] = useState<boolean>(
    false,
  );
  const [agreementType, setAgreementType] = useState<string>('');
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [paymentDate, setPaymentDate] = useState<string>('');
  const [repairDate, setRepairDate] = useState<string>('');
  const [selectWorkshop, setSelectWorkshop] = useState<string>('');
  const [selectAccountType, setSelectAccountType] = useState<string>(
    cleanValue(claimForm?.info?.paymentDetail?.accountType),
  );
  const [agreementFields, setAgreementFields] = useState<any>({});
  const [totalInsuranceAmount, setTotalInsuranceAmount] = useState<string>('');

  const [banks, setBanks] = useState<OptionBank[]>([]);
  const [valueBank, setValueBank] = React.useState<OptionBank | null>(null);
  const [referenceBank, setReferenceBank] = useState<boolean>(
    legacyCurrentClaim?.payment?.referenceBankPolicy || false,
  );
  const [bankPolicy, setBankPolicy] = useState<boolean>(false);
  const [nameBankPolicy, setNameBankPolicy] = useState<string>('');
  const filter = createFilterOptions<OptionBank>();
  const [securityQuestions, setSecurityQuestions] = useState<
    SecurityQuestion[]
  >([]);
  const [disableSaveATHM, setDisableSaveATHM] = useState<boolean>(true);
  const [selectedAnswers, setSelectedAnswers] = useState<{
    [key: number]: boolean;
  }>({});
  const [errorATH, setErrorATH] = useState<string>();
  const [openSaveError, setOpenSaveError] = useState<boolean>(false);

  const defaultPaymentTypes: PaymentType[] = [
    { label: 'Reparación Taller', value: AgreementTypes.REPAIR_CONTINUE },
    {
      label: 'Pago enviado por correo postal',
      value: ReceiveCheckType.POST_MAIL,
    },
    { label: 'Pago cheque oficina', value: ReceiveCheckType.PICKUP_OFFICE },
    { label: 'Pago ACH', value: Payments.ACH },
  ];

  const paymentTypesAccount: TypePayment = {
    [BrandsId.GENERAL_DE_SEGUROS]: [
      { label: 'Reparación Taller', value: AgreementTypes.REPAIR_CONTINUE },
      { label: 'Pago cheque oficina', value: ReceiveCheckType.PICKUP_OFFICE },
      { label: 'Pago ACH', value: Payments.ACH },
    ],
    [BrandsId.MULTINATIONAL]: [
      {
        label: 'Pago enviado por correo postal',
        value: ReceiveCheckType.POST_MAIL,
      },
      { label: 'Pago cheque oficina', value: ReceiveCheckType.PICKUP_OFFICE },
    ],
    [BrandsId.SODA]: [
      {
        label: 'Pago enviado por correo postal',
        value: ReceiveCheckType.POST_MAIL,
      },
      { label: 'Pago cheque oficina', value: ReceiveCheckType.PICKUP_OFFICE },
      { label: 'Pago ACH', value: Payments.ACH },
    ],
    [BrandsId.CHUBB]: [
      {
        label: 'ATH Móvil',
        value: Payments.ATH,
      },
      {
        label: 'Pago enviado por correo postal',
        value: ReceiveCheckType.POST_MAIL,
      },
      { label: 'Pago cheque oficina', value: ReceiveCheckType.PICKUP_OFFICE },
      { label: 'Pago ACH', value: Payments.ACH },
    ],
    [BrandsId.OPTIMA_SEGUROS]: [
      { label: 'Reparación Taller', value: AgreementTypes.REPAIR_CONTINUE },
      {
        label: 'Pago enviado por correo postal',
        value: ReceiveCheckType.POST_MAIL,
      },
      { label: 'Pago cheque oficina', value: ReceiveCheckType.PICKUP_OFFICE },
      { label: 'Pago ACH', value: Payments.ACH },
    ],
    [BrandsId.PURDY]: [
      { label: 'Reparación Taller', value: AgreementTypes.REPAIR_CONTINUE },
    ],
  };

  const getPaymentsType = (companyId: number): PaymentType[] => {
    return paymentTypesAccount[companyId] ?? defaultPaymentTypes;
  };

  const getAgreementType = (claimForm: IClaimForm): void => {
    if (!claimForm || !claimForm?.info?.paymentDetail) {
      setAgreementType('');
    } else {
      localStorage.setItem(
        'agreementType',
        claimForm.info?.paymentDetail?.agreementType ?? '',
      );
      if (
        claimForm.info?.paymentDetail.agreementType ===
        AgreementTypes.REPAIR_CONTINUE
      ) {
        setAgreementType(AgreementTypes.REPAIR_CONTINUE);
      }

      if (
        claimForm.info?.paymentDetail?.agreementType ===
        AgreementTypes.RECEIVE_PAYMENT
      ) {
        if (claimForm.info?.paymentDetail?.paymentType === Payments.CHECK) {
          if (
            claimForm.info?.paymentDetail?.checkType ===
            ReceiveCheckType.POST_MAIL
          ) {
            setAgreementType(ReceiveCheckType.POST_MAIL);
          }

          if (
            claimForm.info?.paymentDetail?.checkType ===
            ReceiveCheckType.PICKUP_OFFICE
          ) {
            setAgreementType(ReceiveCheckType.PICKUP_OFFICE);
          }
        } else if (
          claimForm.info?.paymentDetail?.paymentType === Payments.ACH
        ) {
          setAgreementType(Payments.ACH);
        } else if (
          claimForm.info?.paymentDetail?.paymentType === Payments.ATH
        ) {
          setAgreementType(Payments.ATH);
        }
      }
    }
  };

  const handleDates = (claimForm: IClaimForm): void => {
    if (claimForm.info?.paymentDetail?.paymentDate) {
      setPaymentDate(
        dayjs(claimForm.info?.paymentDetail?.paymentDate).format('DD/MM/YYYY'),
      );
      disableFinishActionState(true);
    }

    if (claimForm.info?.paymentDetail?.repairDate) {
      setRepairDate(
        dayjs(claimForm.info?.paymentDetail?.repairDate).format('DD/MM/YYYY'),
      );
    }

    if (
      !claimForm.info?.paymentDetail?.paymentDate ||
      !claimForm.info?.paymentDetail?.repairDate
    ) {
      disableFinishActionState(true);
    }
  };

  const handleAgreementTypeChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    if (event.target.value === 'ATH') {
      dispatch({ type: ActionType.SET_LOADING, payload: true });
      const secQuestions = await new ClaimTrackerClient().getSecurityQuestions(
        legacyCurrentClaim?.claimUId,
      );

      if (secQuestions) {
        setSecurityQuestions(secQuestions);
      }
      dispatch({ type: ActionType.SET_LOADING, payload: false });
    }

    setAgreementType(event.target.value);

    setValueBank(null);

    setAgreementFields({
      ...buildAchFields(claimForm),
      [event.target.name]: event.target.value,
    });
  };

  const handleWorkShopChange = (event: SelectChangeEvent) => {
    if (event.target.value) {
      setAgreementFields((prev: any) => ({
        ...prev,
        custom_workshop: event.target.value,
      }));
      setSelectWorkshop(event.target.value);
    }
  };

  const handleInputChangeBySupport = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const { name, value } = event.target;

    setAgreementFields((prev: any) => ({ ...prev, [name]: value }));
  };

  const handleAccountTypeChange = (event: SelectChangeEvent) => {
    if (event.target.value) {
      setAgreementFields((prev: any) => ({
        ...prev,
        custom_accountType: event.target.value,
      }));
      setSelectAccountType(event.target.value);
    }
  };

  const selectedAgreementType = (): string =>
    agreementFields['custom_radio-buttons-group'] ?? agreementType;

  const confirmAgreement = (): void => {
    (async (): Promise<void> => {
      setOpenConfirm(false);
      dispatch({ type: ActionType.SET_LOADING, payload: true });
      await submitFinalizeAgreement(
        claimForm?.info?.paymentDetail?.agreementType as AgreementTypes,
      );
    })();
  };

  const requestApproval = async (): Promise<any> => {
    try {
      await new ClaimsTrackerClient().requestApproval(
        claimUId,
        'FINAL_AGREEMENT',
      );
    } catch (e) {
      console.error(e);
    }
  };

  const confirmAgreementBySupport = (): void => {
    (async (): Promise<void> => {
      const agrmntType = selectedAgreementType();
      const achValue = (field: string): string | null =>
        agrmntType === 'ACH'
          ? cleanValue(agreementFields[field]) || null
          : null;
      const payload = {
        agreementType: agrmntType,
        workshop:
          agrmntType === 'CONTINUE_REPAIR'
            ? agreementFields['custom_workshop']
            : null,
        urban:
          agrmntType === 'POST_MAIL'
            ? agreementFields['custom_urbanization']
            : null,
        town:
          agrmntType === 'POST_MAIL' ? agreementFields['custom_town'] : null,
        postalCode:
          agrmntType === 'POST_MAIL'
            ? agreementFields['custom_postalCode']
            : null,
        street:
          agrmntType === 'POST_MAIL'
            ? agreementFields['custom_detailedAddress']
            : null,
        office:
          agrmntType === 'PICKUP_OFFICE'
            ? agreementFields['custom_office']
            : null,
        claimentName: achValue('custom_claimentName'),
        phone: achValue('custom_phone'),
        routeNumber: achValue('custom_routeNumber'),
        nameBank: achValue('custom_nameBank'),
        accountNumber: achValue('custom_accountNumber'),
        accountType: achValue('custom_accountType')?.toUpperCase() ?? null,
        userId: Number(localStorage.getItem('userId')),
        eventId: claimForm?.info?.sicId,
        requiredApproval: legacyCurrentClaim?.requiresApproval,
        bankPolicy: nameBankPolicy === '' ? valueBank?.name : nameBankPolicy,
        referenceBank: referenceBank,
      };
      requestApproval();
      setOpenConfirmBySupport(false);
      dispatch({ type: ActionType.SET_LOADING, payload: true });
      await submitUpdateAgreementBySupport(payload);
    })();
  };

  const confirmEdit = (): void => {
    setOpenConfirmEdit(false);
    setEditByAnalyst(true);
    disableFinishActionState(true);
  };

  const submitFinalizeAgreement = async (
    type: AgreementTypes,
  ): Promise<void> => {
    if (type.length > 0 && selectedDate.length > 0) {
      await new ClaimsTrackerClient().finalizeAgreement(
        claimUId,
        type,
        Number(localStorage.getItem('userId')),
        claimForm?.info?.sicId,
        selectedDate,
      );

      // Re-rendering page
      history.go(0);
    }
  };

  const submitUpdateAgreementBySupport = async (
    updateObj: any,
  ): Promise<void> => {
    if (updateObj) {
      const result = await new ClaimsTrackerClient().updateAgreementBySupport(
        claimUId,
        updateObj,
      );

      if (result?.success === false) {
        if (updateObj.agreementType === 'ATH') {
          handleATHErrors(result);
        } else {
          console.error('Error al guardar el acuerdo final', result.message);
          setOpenSaveError(true);
        }
        dispatch({ type: ActionType.SET_LOADING, payload: false });

        return;
      }

      // Re-rendering page
      history.go(0);
    }
  };

  const getBanksData = async (): Promise<void> => {
    try {
      const bank = await getBanks(claimForm?.info?.countryId);
      setBanks(bank);
      if (claimForm?.info?.policy?.bank) {
        const bankName = claimForm?.info?.policy?.bank;

        if (bankName.replace(/-/g, '').replace(/\s/g, '') !== '') {
          setNameBankPolicy(bankName);
          setBankPolicy(true);
        }
      }
    } catch (e) {
      console.error(e);
    }
  };
  const handleATHErrors = (result: any) => {
    alert(JSON.stringify(result));

    let errorMsg = 'An unexpected error occurred';
    try {
      const errorData = result.message;
      if (errorData.errors?.ATHMRetries) {
        errorMsg =
          'Ups! Has realizado demasiados intentos, Elige nuevamente una opción o comunícate con cabina para recibir ayuda.';
      } else if (errorData.errors?.Conflict) {
        const conflictMessage = JSON.parse(errorData.errors.Conflict[0]);
        errorMsg = conflictMessage.message;
      } else if (errorData.title) {
        errorMsg = errorData.title;
      }
      setErrorATH(errorMsg);
    } catch (parseError) {
      console.error('Error parsing response:', parseError);
    }
  };

  const getAdjustAmount = async (): Promise<void> => {
    try {
      dispatch({
        type: ActionType.SET_ADJUST_AMOUNT,
        payload: undefined,
      });
      const result = await new ClaimsTrackerClient().getAllAdjustAmount(
        claimUId,
        claimForm?.info?.totalLoss?.isApply,
      );
      if (result?.length > 0) {
        const data = result.slice(-1)[0];
        setTotalInsuranceAmount(
          formatNumberPoint(
            claimForm?.info?.totalLoss?.isApply
              ? data.totalToPay.toString()
              : data.totalInsuranceAmount,
          ),
        );
      }
    } catch (e) {
      console.error(e);
    }
  };
  const skeletonLoader = (): JSX.Element => {
    return (
      <>
        <br />
        <Skeleton variant="rectangular" height={450} color="#7d92a1" />
      </>
    );
  };
  useEffect(() => {
    getAdjustAmount().catch(console.error);
    getBanksData().catch(console.error);
  }, []);

  useEffect(() => {
    getAdjustAmount().catch(console.error);
  }, [claimForm?.info?.case]);

  useEffect(() => {
    if (claimForm) {
      getAgreementType(claimForm);
      handleDates(claimForm);

      setSelectAccountType(
        (prev) =>
          prev || cleanValue(claimForm.info?.paymentDetail?.accountType),
      );
    }
  }, [claimForm]);

  useEffect(() => {
    if (legacyCurrentClaim) {
      setReferenceBank(
        legacyCurrentClaim?.payment?.referenceBankPolicy || false,
      );
    }
  }, [legacyCurrentClaim]);

  const handleSelectionChange = (questionId: number) => {
    setSelectedAnswers((prev) => {
      const updatedAnswers = {
        ...prev,
        [questionId]: !prev[questionId], // Alterna entre seleccionado y no seleccionado
      };

      // Verificar si todas las preguntas han sido respondidas (true)
      const allSelected = securityQuestions.every(
        (q) => updatedAnswers[q.questionId ?? 0],
      );
      setDisableSaveATHM(!allSelected);

      return updatedAnswers;
    });
  };

  // Purdy CR: No mostrar formulario de pago/taller/montos
  if (
    Number(legacyCurrentClaim?.jsonFieldData?.event?.CompanyId) ===
    BrandsId.PURDY
  ) {
    return <></>;
  }

  return (
    <>
      <Box className="agreement-detail">
        {!claimForm
          ? skeletonLoader()
          : (editByAnalyst ||
              (agreementType?.length > 0 &&
                !legacyCurrentClaim?.closedWithoutPayment)) && (
              <Box>
                <TextField
                  name="estimatedTotalAmount"
                  className="claim-adjust__data--row--input"
                  label="Monto total asegurado"
                  value={totalInsuranceAmount}
                  disabled
                ></TextField>
                <br />
                <p className="agreement-detail__data--title">Tipo de Acuerdo</p>
                <RadioGroup
                  row
                  defaultValue={agreementType}
                  name="custom_radio-buttons-group"
                  onChange={handleAgreementTypeChange}
                >
                  {legacyCurrentClaim?.payToBank !== true &&
                    legacyCurrentClaim?.applyATHM &&
                    [
                      BrandsId.PREMIER,
                      BrandsId.SODA,
                      BrandsId.OPTIMA_SEGUROS,
                    ].includes(
                      legacyCurrentClaim?.jsonFieldData?.event
                        .CompanyId as BrandsId,
                    ) && (
                      <FormControlLabel
                        value={Payments.ATH}
                        control={<Radio />}
                        label="ATH Móvil"
                        disabled={!editByAnalyst}
                      />
                    )}

                  {legacyCurrentClaim?.payToBank === true ? (
                    <>
                      <FormControlLabel
                        value={ReceiveCheckType.POST_MAIL}
                        control={<Radio />}
                        label="Pago enviado por correo postal"
                        disabled={!editByAnalyst}
                      />
                      <FormControlLabel
                        value={ReceiveCheckType.PICKUP_OFFICE}
                        control={<Radio />}
                        label="Pago cheque oficina"
                        disabled={!editByAnalyst}
                      />
                    </>
                  ) : (
                    getPaymentsType(
                      Number(legacyCurrentClaim?.accountReference),
                    ).map((paymentType, index) => {
                      return (
                        <FormControlLabel
                          key={index}
                          value={paymentType.value}
                          control={<Radio />}
                          label={paymentType.label}
                          disabled={!editByAnalyst}
                        />
                      );
                    })
                  )}
                </RadioGroup>
              </Box>
            )}

        {agreementType === AgreementTypes.REPAIR_CONTINUE &&
          !legacyCurrentClaim?.closedWithoutPayment && (
            <Box>
              {editByAnalyst && (
                <Box>
                  <br />
                  <FormControl
                    sx={{ minWidth: 235, backgroundColor: '#ffffff' }}
                  >
                    <InputLabel id="workshopLbl">Seleccione Taller</InputLabel>
                    <Select
                      labelId="workshopLbl"
                      id="slct-WorkshopLbl"
                      value={selectWorkshop ?? 0}
                      onChange={handleWorkShopChange}
                      label="Seleccione Taller"
                      disabled={disabled}
                    >
                      {claimState.workshopsInfo?.map(
                        (workshop: IWorkshopInfo) => {
                          return (
                            <MenuItem
                              key={workshop.WorkshopId}
                              value={workshop.WorkshopId}
                            >
                              {workshop.Name}
                            </MenuItem>
                          );
                        },
                      )}
                    </Select>
                  </FormControl>
                </Box>
              )}

              {!editByAnalyst && (
                <>
                  <Box>
                    <br />
                    <Field
                      key="workshop"
                      as={TextField}
                      type="text"
                      label="Nombre de Taller"
                      name="workshop"
                      defaultValue={
                        claimForm?.info?.paymentDetail?.workShop?.name
                      }
                      disabled={true}
                    />
                  </Box>
                  <br />
                  <Box className="agreement-detail__data">
                    {repairDate.length > 0 ? (
                      <>
                        <Box>
                          <p className="agreement-detail__data--title-result">
                            Reparación realizada
                          </p>
                          <p className="agreement-detail__data--subtitle-result">
                            Fecha de Reparación: {repairDate}
                          </p>
                        </Box>
                      </>
                    ) : (
                      <>
                        <Box>
                          <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DesktopDatePicker
                              label="Fecha Finaliza Reparación"
                              format={
                                CATALOGS.getCountryFormat().dateTimeFormat
                              }
                              disabled={disabled}
                              slotProps={{
                                textField: {
                                  variant: 'outlined',
                                  margin: 'none',
                                  'aria-label': 'change date',
                                },
                              }}
                              onChange={(date: any) => {
                                if (date) {
                                  const dayjsDate = dayjs(date);
                                  if (dayjsDate.isValid()) {
                                    setSelectedDate(
                                      dayjsDate.format('YYYY-MM-DD'),
                                    );
                                  }
                                }
                              }}
                            />
                          </LocalizationProvider>
                        </Box>
                        <Box>
                          <Button
                            text="Reparación Realizada"
                            type="button"
                            disabled={
                              disabled == true ||
                              claimForm?.info.currentStepStatus ==
                                'PENDING_APPROVAL'
                            }
                            onClick={() => setOpenConfirm(true)}
                          />
                        </Box>
                      </>
                    )}
                  </Box>
                </>
              )}
            </Box>
          )}

        <>
          {agreementType === ReceiveCheckType.POST_MAIL &&
          !legacyCurrentClaim?.closedWithoutPayment ? (
            <Box className="agreement-detail__data">
              <Box>
                <br />
                <Field
                  key="urbanization"
                  as={TextField}
                  type="text"
                  label="Urb, Barrio, Edificio..."
                  name="custom_urbanization"
                  onChange={handleInputChangeBySupport}
                  defaultValue={
                    editByAnalyst
                      ? ''
                      : claimForm?.info?.paymentDetail?.urbanization ?? 'N/D'
                  }
                  disabled={!editByAnalyst}
                  inputProps={{ maxLength: 300 }}
                />
              </Box>
              <Box>
                <br />
                <Field
                  key="town"
                  as={TextField}
                  type="text"
                  label="Pueblo"
                  name="custom_town"
                  onChange={handleInputChangeBySupport}
                  defaultValue={
                    editByAnalyst
                      ? ''
                      : claimForm?.info?.paymentDetail?.town ?? 'N/D'
                  }
                  disabled={!editByAnalyst}
                  inputProps={{ maxLength: 200 }}
                />
              </Box>
              <Box>
                <br />
                <Field
                  key="detailedAddress"
                  as={TextField}
                  type="text"
                  label="Número de casa o Apart, Calle o Av. "
                  name="custom_detailedAddress"
                  onChange={handleInputChangeBySupport}
                  defaultValue={
                    editByAnalyst
                      ? ''
                      : claimForm?.info?.paymentDetail?.detailedAddress ?? 'N/D'
                  }
                  disabled={!editByAnalyst}
                  inputProps={{ maxLength: 300 }}
                />
              </Box>
              <Box>
                <br />
                <Field
                  key="postalCode"
                  as={TextField}
                  type="text"
                  label="Código Postal"
                  name="custom_postalCode"
                  onChange={handleInputChangeBySupport}
                  defaultValue={
                    editByAnalyst
                      ? ''
                      : claimForm?.info?.paymentDetail?.postalCode ?? 'N/D'
                  }
                  disabled={!editByAnalyst}
                  inputProps={{ maxLength: 50 }}
                />
              </Box>
              {legacyCurrentClaim?.payToBank !== true && (
                <>
                  <br />
                  <Box>
                    <p className="agreement-detail__data--title">
                      ¿Requiere referenciar el banco de la póliza?
                    </p>
                    <RadioGroup
                      row
                      value={referenceBank}
                      onChange={(e) =>
                        setReferenceBank(e.target.value === 'true')
                      }
                    >
                      <FormControlLabel
                        value={true}
                        control={<Radio />}
                        label="Sí"
                        disabled={!editByAnalyst}
                      />
                      <FormControlLabel
                        value={false}
                        control={<Radio />}
                        label="No"
                        disabled={!editByAnalyst}
                      />
                    </RadioGroup>
                  </Box>
                </>
              )}
              {(legacyCurrentClaim?.payToBank === true ||
                referenceBank === true) && (
                <Box>
                  <br />
                  {bankPolicy ? (
                    <Field
                      key="bankPolicy"
                      as={TextField}
                      type="text"
                      label="Banco relacionado en la póliza"
                      name="bankPolicy"
                      value={nameBankPolicy}
                      defaultValue={nameBankPolicy}
                      disabled={true}
                    />
                  ) : (
                    <Autocomplete
                      style={{ backgroundColor: 'white', margin: '0' }}
                      value={valueBank}
                      disabled={!editByAnalyst}
                      id="bankPolicy"
                      onChange={(_, newValue) => {
                        let valueFinal = '';
                        if (typeof newValue === 'string') {
                          setValueBank({ name: newValue });
                          valueFinal = newValue;
                        } else if (newValue && newValue.inputValue) {
                          setValueBank({ name: newValue.inputValue });
                          valueFinal = newValue.inputValue;
                        } else {
                          setValueBank(newValue);
                          valueFinal =
                            newValue === undefined || newValue === null
                              ? ''
                              : newValue.name;
                        }
                        setAgreementFields((prev: any) => ({
                          ...prev,
                          custom_office: valueFinal,
                        }));
                      }}
                      options={banks}
                      filterOptions={(options, params) => {
                        const filtered = filter(options, params);

                        const { inputValue } = params;
                        const isExisting = options.some(
                          (option) => inputValue === option.name,
                        );
                        if (inputValue !== '' && !isExisting) {
                          filtered.push({
                            inputValue,
                            name: `Agregar "${inputValue}"`,
                          });
                        }

                        return filtered;
                      }}
                      selectOnFocus
                      clearOnBlur
                      handleHomeEndKeys
                      getOptionLabel={(option) => {
                        if (typeof option === 'string') {
                          return option;
                        }
                        if (option.inputValue) {
                          return option.inputValue;
                        }

                        return option.name;
                      }}
                      renderOption={(props, option) => {
                        const { id, key, ...optionProps } = props; // eslint-disable-line @typescript-eslint/no-unused-vars

                        return (
                          <li key={id} {...optionProps}>
                            {option.name}
                          </li>
                        );
                      }}
                      sx={{ width: 400 }}
                      freeSolo
                      renderInput={(params) => (
                        <TextField
                          sx={{ minWidth: 400 }}
                          {...params}
                          label="Banco relacionado en la póliza"
                        />
                      )}
                    />
                  )}
                </Box>
              )}
            </Box>
          ) : (
            <>
              {agreementType === ReceiveCheckType.PICKUP_OFFICE &&
              !legacyCurrentClaim?.closedWithoutPayment ? (
                <>
                  {legacyCurrentClaim?.payToBank !== true && (
                    <>
                      <br />
                      <Box>
                        <p className="agreement-detail__data--title">
                          ¿Requiere referenciar el banco de la póliza?
                        </p>
                        <RadioGroup
                          row
                          value={referenceBank}
                          onChange={(e) =>
                            setReferenceBank(e.target.value === 'true')
                          }
                        >
                          <FormControlLabel
                            value={true}
                            control={<Radio />}
                            label="Sí"
                            disabled={!editByAnalyst}
                          />
                          <FormControlLabel
                            value={false}
                            control={<Radio />}
                            label="No"
                            disabled={!editByAnalyst}
                          />
                        </RadioGroup>
                      </Box>
                    </>
                  )}
                  {(legacyCurrentClaim?.payToBank === true ||
                    referenceBank === true) && (
                    <Box>
                      <br />
                      {bankPolicy ? (
                        <Field
                          key="bankPolicy"
                          as={TextField}
                          type="text"
                          label="Banco relacionado en la póliza"
                          name="bankPolicy"
                          value={nameBankPolicy}
                          defaultValue={nameBankPolicy}
                          disabled={true}
                        />
                      ) : (
                        <Autocomplete
                          style={{ backgroundColor: 'white', margin: '0' }}
                          value={valueBank}
                          disabled={!editByAnalyst}
                          id="bankPolicy"
                          onChange={(_, newValue) => {
                            let valueFinal = '';
                            if (typeof newValue === 'string') {
                              setValueBank({ name: newValue });
                              valueFinal = newValue;
                            } else if (newValue && newValue.inputValue) {
                              setValueBank({ name: newValue.inputValue });
                              valueFinal = newValue.inputValue;
                            } else {
                              setValueBank(newValue);
                              valueFinal =
                                newValue === undefined || newValue === null
                                  ? ''
                                  : newValue.name;
                            }
                            setAgreementFields((prev: any) => ({
                              ...prev,
                              custom_office: valueFinal,
                            }));
                          }}
                          options={banks}
                          filterOptions={(options, params) => {
                            const filtered = filter(options, params);

                            const { inputValue } = params;
                            const isExisting = options.some(
                              (option) => inputValue === option.name,
                            );
                            if (inputValue !== '' && !isExisting) {
                              filtered.push({
                                inputValue,
                                name: `Agregar "${inputValue}"`,
                              });
                            }

                            return filtered;
                          }}
                          selectOnFocus
                          clearOnBlur
                          handleHomeEndKeys
                          getOptionLabel={(option) => {
                            if (typeof option === 'string') {
                              return option;
                            }
                            if (option.inputValue) {
                              return option.inputValue;
                            }

                            return option.name;
                          }}
                          renderOption={(props, option) => {
                            const { id, key, ...optionProps } = props; // eslint-disable-line @typescript-eslint/no-unused-vars

                            return (
                              <li key={id} {...optionProps}>
                                {option.name}
                              </li>
                            );
                          }}
                          sx={{ width: 400 }}
                          freeSolo
                          renderInput={(params) => (
                            <TextField
                              sx={{ minWidth: 400 }}
                              {...params}
                              label="Banco relacionado en la póliza"
                            />
                          )}
                        />
                      )}
                    </Box>
                  )}
                </>
              ) : (
                <>
                  {agreementType === Payments.ACH &&
                    !legacyCurrentClaim?.closedWithoutPayment && (
                      <>
                        <Box className="agreement-detail__data">
                          <Box>
                            <br />
                            <Field
                              key="claimentName"
                              as={TextField}
                              type="text"
                              label="Nombre dueño cuenta"
                              name="custom_claimentName"
                              onChange={handleInputChangeBySupport}
                              defaultValue={
                                claimForm?.info?.paymentDetail?.claimentName ??
                                'N/D'
                              }
                              disabled={!editByAnalyst}
                              inputProps={{ maxLength: 200 }}
                            />
                          </Box>
                          <Box>
                            <br />
                            <Field
                              key="phone"
                              as={TextField}
                              type="text"
                              label="Teléfono"
                              name="custom_phone"
                              onChange={handleInputChangeBySupport}
                              defaultValue={
                                claimForm?.info?.paymentDetail?.phone ?? 'N/D'
                              }
                              disabled={!editByAnalyst}
                              inputProps={{ maxLength: 20 }}
                            />
                          </Box>
                          <Box>
                            <br />
                            <Field
                              key="routeNumber"
                              as={TextField}
                              type="text"
                              label="Número de ruta/tránsito"
                              name="custom_routeNumber"
                              onChange={handleInputChangeBySupport}
                              defaultValue={
                                claimForm?.info?.paymentDetail?.routeNumber ??
                                'N/D'
                              }
                              disabled={!editByAnalyst}
                              inputProps={{ maxLength: 50 }}
                            />
                          </Box>
                          <Box>
                            <br />
                            <Field
                              key="nameBank"
                              as={TextField}
                              type="text"
                              label="Nombre institución financiera"
                              name="custom_nameBank"
                              onChange={handleInputChangeBySupport}
                              defaultValue={
                                claimForm?.info?.paymentDetail?.nameBank ??
                                'N/D'
                              }
                              inputProps={{ maxLength: 200 }}
                            />
                          </Box>
                          <Box>
                            <br />
                            <Field
                              key="accountNumber"
                              as={TextField}
                              type="text"
                              label="# Cuenta"
                              name="custom_accountNumber"
                              onChange={handleInputChangeBySupport}
                              defaultValue={
                                claimForm?.info?.paymentDetail?.accountNumber ??
                                'N/D'
                              }
                              disabled={!editByAnalyst}
                              inputProps={{ maxLength: 50 }}
                            />
                          </Box>
                          <Box>
                            <br />
                            <FormControl
                              sx={{ minWidth: 175, backgroundColor: '#ffffff' }}
                            >
                              <InputLabel id="custom_accountTypeLabel">
                                Tipo de Cuenta
                              </InputLabel>
                              <Select
                                labelId="custom_accountTypeLabel"
                                id="custom_accountType"
                                value={selectAccountType}
                                onChange={handleAccountTypeChange}
                                label="Tipo de Cuenta"
                                disabled={!editByAnalyst}
                              >
                                <MenuItem value={'C'}>Corriente</MenuItem>
                                <MenuItem value={'A'}>Ahorro</MenuItem>
                              </Select>
                            </FormControl>
                          </Box>
                        </Box>
                        {!editByAnalyst && (
                          <Box className="agreement-detail__signature">
                            <br />
                            <Signature
                              signature={
                                claimForm?.info?.paymentDetail
                                  ?.agreementSignature ?? ''
                              }
                              isInspectorForm={false}
                              isDisabled={true}
                              onSignatureChange={() => {
                                console.log('Signature is read only');
                              }}
                            />
                          </Box>
                        )}
                      </>
                    )}
                </>
              )}
            </>
          )}

          {agreementType.length > 0 && agreementType === Payments.ATH && (
            <>
              <QuestionList
                title="PREGUNTAS DE SEGURIDAD"
                questions={securityQuestions}
                selectedAnswers={selectedAnswers}
                onSelectionChange={handleSelectionChange}
              />
            </>
          )}

          {agreementType.length > 0 &&
            agreementType !== AgreementTypes.REPAIR_CONTINUE &&
            !legacyCurrentClaim?.closedWithoutPayment && (
              <>
                {claimForm?.info?.paymentDetail?.paymentType ===
                Payments.ATH ? (
                  <Box>
                    <p className="agreement-detail__data--title-result">
                      Pago realizado
                    </p>
                    <p className="agreement-detail__data--subtitle-result">
                      Número de celular: {legacyCurrentClaim?.contactPhone}
                    </p>
                    <p className="agreement-detail__data--subtitle-result">
                      Fecha de Pago:{' '}
                      {legacyCurrentClaim?.payment?.paymentDate
                        ?.toString()
                        .substring(0, 10)}
                    </p>
                  </Box>
                ) : (
                  <Box className="agreement-detail__data">
                    {paymentDate.length > 0 ? (
                      <>
                        <Box>
                          <p className="agreement-detail__data--title-result">
                            Pago realizado
                          </p>
                          <p className="agreement-detail__data--subtitle-result">
                            Fecha de Pago: {paymentDate}
                          </p>
                        </Box>
                      </>
                    ) : (
                      <>
                        {!editByAnalyst && (
                          <>
                            <Box>
                              <br />
                              <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DesktopDatePicker
                                  label="Fecha de Pago"
                                  disabled={disabled}
                                  format={
                                    CATALOGS.getCountryFormat().dateTimeFormat
                                  }
                                  slotProps={{
                                    textField: {
                                      variant: 'outlined',
                                      margin: 'none',
                                      'aria-label': 'change date',
                                    },
                                  }}
                                  onChange={(date: any) => {
                                    if (date) {
                                      const dayjsDate = dayjs(date);
                                      if (dayjsDate.isValid()) {
                                        setSelectedDate(
                                          dayjsDate.format('YYYY-MM-DD'),
                                        );
                                      }
                                    }
                                  }}
                                />
                              </LocalizationProvider>
                            </Box>
                            <Box>
                              <br />
                              <Button
                                text="Pago Realizado"
                                type="button"
                                disabled={
                                  disabled == true ||
                                  claimForm?.info.currentStepStatus ==
                                    'PENDING_APPROVAL'
                                }
                                onClick={() => setOpenConfirm(true)}
                              />
                            </Box>
                          </>
                        )}
                      </>
                    )}
                  </Box>
                )}
              </>
            )}
          {errorATH && (
            <Box>
              <p className="agreement-detail__data--title-result">
                ¡Ups!, Error en el sistema
              </p>
              <p className="agreement-detail__data--subtitle-result">
                {errorATH}
              </p>
            </Box>
          )}
        </>

        {editByAnalyst && !legacyCurrentClaim?.closedWithoutPayment && (
          <Box>
            <br />
            <Button
              text="Guardar Cambios"
              type="button"
              disabled={
                disabled ||
                !selectedAgreementType() ||
                (agreementType === Payments.ATH && disableSaveATHM)
              }
              onClick={() => setOpenConfirmBySupport(true)}
            />
          </Box>
        )}

        {claimForm &&
          !editByAnalyst &&
          legacyCurrentClaim?.closedWithoutPayment && (
            <>
              <Stack sx={{ width: '100%' }} spacing={2}>
                <Alert severity="info">
                  <AlertTitle>
                    <strong>Cierre sin pago</strong>
                  </AlertTitle>
                </Alert>
              </Stack>
            </>
          )}

        {claimForm &&
          !editByAnalyst &&
          claimForm?.info?.paymentDetail?.agreementType?.length == 0 &&
          !legacyCurrentClaim?.closedWithoutPayment && (
            <>
              <Stack sx={{ width: '100%' }} spacing={2}>
                <Alert severity="warning">
                  <AlertTitle>
                    <strong>Falta de Información</strong>
                  </AlertTitle>
                  Reclamante aún no define los parámetros del acuerdo para el{' '}
                  <strong>pago / reparación</strong>.
                  <br />
                  <Button
                    className="agreement-detail__btn-alert"
                    style={{ marginTop: '1rem' }}
                    text="Actualizar Información por Solicitud"
                    type="button"
                    onClick={() => setOpenConfirmEdit(true)}
                    disabled={disabled}
                    size="small"
                  />
                </Alert>
              </Stack>
            </>
          )}
      </Box>
      <Dialog open={openConfirm}>
        <DialogTitle id="confirm-restore-title">Confirmar</DialogTitle>
        <DialogContent>
          <DialogContentText
            id="confirm-payment-modal"
            style={{ width: '400px', height: '120px' }}
          >
            <br />
            ¿Está seguro que desea aplicar la finalización para esta fecha?
            <br />
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            text="Cancelar"
            variant="outlined"
            color="secondary"
            onClick={() => setOpenConfirm(false)}
          />
          <Button
            text="Sí, confirmar"
            variant="contained"
            color="primary"
            onClick={confirmAgreement}
          />
        </DialogActions>
      </Dialog>
      <Dialog open={openConfirmEdit}>
        <DialogTitle id="confirm-restore-title">Confirmar Edición</DialogTitle>
        <DialogContent>
          <DialogContentText
            id="confirm-payment-modal"
            style={{ width: '400px', height: '120px' }}
          >
            <br />
            ¿Está seguro que desea{' '}
            <b>actualizar la información del reclamante</b>?
            <br />
            <p>
              <strong>Nota:</strong> Esta acción quedará registrada en el
              histórico del caso.
            </p>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            text="Cancelar"
            variant="outlined"
            color="secondary"
            onClick={() => setOpenConfirmEdit(false)}
          />
          <Button
            text="Sí, confirmar"
            variant="contained"
            color="primary"
            onClick={confirmEdit}
          />
        </DialogActions>
      </Dialog>
      <Dialog open={openConfirmBySupport}>
        <DialogTitle id="confirm-restore-title2">
          Confirmar cambios vía solicitud
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            id="confirm-payment-modal2"
            style={{ width: '400px', height: '120px' }}
          >
            <br />
            ¿Desea aplicar los cambios actuales para este reclamante?
            <br />
            <p>
              <strong>Nota:</strong> Esta acción quedará registrada en el
              histórico del caso.
            </p>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            text="Cancelar"
            variant="outlined"
            color="secondary"
            onClick={() => setOpenConfirmBySupport(false)}
          />
          <Button
            text="Sí, confirmar"
            variant="contained"
            color="primary"
            onClick={confirmAgreementBySupport}
          />
        </DialogActions>
      </Dialog>
      <Dialog open={openSaveError}>
        <DialogTitle id="save-error-title">
          No se guardaron los cambios
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            id="save-error-modal"
            style={{ width: '400px', height: '120px' }}
          >
            <br />
            No se pudo completar el guardado del acuerdo final. Revisa la
            información e intenta de nuevo.
            <br />
            <p>
              <strong>Nota:</strong> Si el problema persiste, reporta el caso a
              soporte antes de continuar con el pago.
            </p>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            text="Entendido"
            variant="contained"
            color="primary"
            onClick={() => setOpenSaveError(false)}
          />
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AgreementDetail;
